import moment from 'moment';
import SimpleModel from './SimpleModel';
import { toISOLanguageCode } from '../../components/changeLanguage/isoLanguageCode';

/**
 * TaskInfo class is responsible for managing task-related information and operations.
 * It integrates with various services and utilities to provide a comprehensive task management system.
 */
class TaskInfo extends SimpleModel {
    /**
     * Constructor for TaskInfo class.
     * @param {Object} SimpleModel - The simple model service.
     * @param {Object} $translate - Angular translate service.
     * @param {Object} $timeout - Angular timeout service.
     * @param {Object} $rootScope - Angular root scope service.
     * @param {Object} data - Initial data for the task.
     */
    constructor(SimpleModel, $translate, $timeout, $rootScope, data) {
        super(data);
        this.SimpleModel = SimpleModel;
        this.$translate = $translate;
        this.$timeout = $timeout;        
        this.$rootScope = $rootScope;
        this.data = data;
        this.init();
    }

    /**
     * Pads a numeric value with a leading zero if it is less than 10.
     * @param {number} value - The numeric value to pad.
     * @returns {string} - The padded string.
     */
    tzpad(value) {
        return value < 10 ? '0' + value : value.toString();
    }

    /**
     * Creates a string representation of the current timezone offset.
     * @returns {string} - The timezone offset in the format [+-]HH:MM.
     */
    createCurrentTimeZoneOffset() {
        const date = new Date();
        const sign = (date.getTimezoneOffset() > 0) ? "-" : "+";
        const offset = Math.abs(date.getTimezoneOffset());
        const hours = this.tzpad(Math.floor(offset / 60));
        const minutes = this.tzpad(offset % 60);
        return sign + hours + ":" + minutes;
    }

    /**
     * Safely converts a date string to a Date object, adjusting for the current timezone.
     * @param {string} dateStr - The date string to convert.
     * @returns {Date|null|undefined} - The Date object, null if the string is empty, or undefined if it is not provided.
     */
    safeDate(dateStr) {
        if (typeof dateStr === 'undefined') return dateStr;
        if (dateStr == null) return dateStr;
        if (dateStr === '') return null;
        return new Date(dateStr + this.createCurrentTimeZoneOffset());
    }

    /**
     * Initializes the TaskInfo instance by setting up readable and accessible properties and configuring internationalization.
     * @returns {TaskInfo} - The current instance of TaskInfo.
     */
    init() {
        this.readables({
            'readingtime': Number,
            'beforeAssessmentStart': Number,
            'afterAssessmentStart': Number,
            'created_on': String,
            'subject': String,
            'session': String,
            'begin': String,
            'expire': String,
            'title': String,
            'tasks': Array,
            'taskDataFiles': Array,
            'taskScriptFiles': Array,
            'inputLanguage': Object,
            'eventTypes': Array,
            'lossOfFocusConfig': Array,
            'uploadConfig': Object,
            'packageType': String,
            'secondaryAccessCode': String,
            'systemLangSelectable': Boolean,
            'customMarkLabel': String,
            'customMarksLabel': String,
            'mediaAssessment': Boolean,
            'totalMarks': Number,
            'alternativeLossOfFocus': Boolean
        });
        this.accessables({
            'languageCode': String
        });

        const i18n = this.getISOLanguageCode();
        if (this.getISOLanguageCode() !== '_') {
            moment.locale(i18n);
            this.$timeout(() => {
                this.$translate.use(i18n);
            }, 1);
        }

        this.$rootScope.i18n = {
            languageCode: this.data.languageCode,
            ISOLanguageCode: this.getISOLanguageCode()
        };

        return this;
    }

    /**
     * Converts the reading time from minutes to milliseconds.
     * @returns {number} - The reading time in milliseconds.
     */
    getReadingtimeMillis() {
        return this.getReadingtime() * 60000;
    }

    /**
     * Checks if the secondary access has expired.
     * @returns {boolean} - True if the secondary access has expired, false otherwise.
     */
    isSecondaryAccessExpired() {
        if (this.isExpired() && moment(this.getExpireDate()).add(this.getAfterAssessmentStart(), 'hours').isAfter(moment())) {
            return true;
        }
        return false;
    }

    /**
     * Checks if the invigilator login is accessible.
     * @returns {boolean} - True if the invigilator login is accessible, false otherwise.
     */
    isInvigilatorLoginAccessable() {
        return !this.isBegun() && moment(this.getBeginDate()).subtract(this.getBeforeAssessmentStart(), 'hours').isBefore(moment());
    }

    /**
     * Gets the begin date of the task.
     * @returns {Date} - The begin date.
     */
    getBeginDate() {
        return this.safeDate(this.getBegin());
    }

    /**
     * Gets the expire date of the task.
     * @returns {Date} - The expire date.
     */
    getExpireDate() {
        return this.safeDate(this.getExpire());
    }

    /**
     * Checks if the task has expired.
     * @returns {boolean} - True if the task has expired, false otherwise.
     */
    isExpired() {
        const ed = this.getExpireDate();
        if (typeof ed === 'undefined') return false;
        return ed.getTime() <= new Date().getTime();
    }

    /**
     * Checks if the task has begun.
     * @returns {boolean} - True if the task has begun, false otherwise.
     */
    isBegun() {
        const bd = this.getBeginDate();
        if (typeof bd === 'undefined') return true;
        return new Date().getTime() >= bd.getTime();
    }

    /**
     * Checks if the current time is within the task timeframe.
     * @returns {boolean} - True if the current time is within the timeframe, false otherwise.
     */
    isInTimeframe() {
        return this.isBegun() && !this.isExpired();
    }

    /**
     * Retrieves task data by UUID.
     * @param {string} uuid - The UUID of the task.
     * @returns {Object|undefined} - The task data object if found, undefined otherwise.
     */
    getTaskDataByUUID(uuid) {
        const tasks = this.getTasks();
        const taskUUID = uuid;
        let task;

        for (let ti in tasks) {
            if (tasks[ti].uuid === taskUUID) {
                task = tasks[ti];
                break;
            }
        }
        return task;
    }

    /**
     * Gets the ISO language code based on the language code provided in the data.
     * @returns {string} - The ISO language code.
     */
    getISOLanguageCode() {
        return toISOLanguageCode(this.data.languageCode);
    }

    /**
     * Checks if the input language code is defined.
     * @returns {boolean} - True if the input language code is defined, false otherwise.
     */
    isInputLanguageCode() {
        if (typeof this.data.inputLanguage !== 'undefined' && typeof this.data.inputLanguage.inputLanguageCode !== 'undefined' && this.data.inputLanguage.inputLanguageCode) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Gets the ISO input language code.
     * @returns {string} - The ISO input language code.
     */
    getISOInputLanguageCode() {
        if (!this.isInputLanguageCode()) {
            return this.getISOLanguageCode();
        }
        const languageCode = this.data.inputLanguage.inputLanguageCode ? this.data.inputLanguage.inputLanguageCode : 'en_US';
        return languageCode + '_' + (this.data.inputLanguage.locale ? this.data.inputLanguage.locale.toUpperCase() : languageCode.toUpperCase());
    }

    /**
     * Gets the short ISO input language code.
     * @returns {string} - The short ISO input language code.
     */
    getISOInputLanguageCodeShort() {
        if (!this.isInputLanguageCode()) {
            return this.getISOLanguageCode();
        }
        const languageCode = this.data.inputLanguage.inputLanguageCode ? this.data.inputLanguage.inputLanguageCode : 'en';
        return languageCode;
    }

    /**
     * Gets the writing direction based on the input language.
     * @returns {string} - The writing direction ('ltr' or 'rtl').
     */
    getWritingDirection() {
        if (typeof this.data.inputLanguage === 'undefined' || typeof this.data.inputLanguage.writingDirection === 'undefined') {
            return 'ltr';
        }
        return this.data.inputLanguage.writingDirection;
    }

    /**
     * Sets the writing direction for the input language.
     * @param {string} val - The writing direction value ('ltr' or 'rtl').
     */
    setWritingDirection(val) {
        this.data.inputLanguage.writingDirection = val;
    }

    /**
     * Gets the spellchecker language code.
     * @returns {string} - The spellchecker language code.
     */
    getSpellcheckerLanguageCode() {
        if (typeof this.data.inputLanguage === 'undefined' || typeof this.data.inputLanguage.spellcheckerLanguageCode === 'undefined') {
            return this.data.languageCode;
        }
        return this.data.inputLanguage.spellcheckerLanguageCode;
    }

    /**
     * Sets the input language code.
     * @param {string} val - The input language code.
     */
    setInputLanguage(val) {
        if (!angular.isObject(this.data.inputLanguage)) {
            this.data.inputLanguage = {};
        }
        this.data.inputLanguage.inputLanguageCode = val;
    }

    /**
     * Sets the spellchecker language code.
     * @param {string} val - The spellchecker language code.
     */
    setSpellcheckerLanguageCode(val) {
        this.data.inputLanguage.spellcheckerLanguageCode = val;
    }

    /**
     * Gets the UUID of the first task.
     * @returns {string} - The UUID of the first task.
     */
    cms_getUUID() {
        return this.getTasks()[0].uuid;
    }

    /**
     * Gets the subject or title of the task.
     * @returns {string} - The subject if it is a non-empty string, otherwise the title.
     */
    getSubjectOrTitle() {
        const subject = this.getSubject();
        if (typeof subject === 'string' && subject !== '') {
            return subject;
        } else {
            return this.getTitle();
        }
    }
}

export default TaskInfo;